import { ARENA, getActiveObstacles } from '../map/arena.js';
import type { ObstacleRect } from '../map/arena.js';
import type { Vec2 } from '../types.js';

export interface Circle {
  x: number;
  y: number;
  r: number;
}

/** Clamp a circle centre so the whole circle stays inside the arena bounds. */
export function clampToBounds(c: Circle): void {
  c.x = Math.max(c.r, Math.min(ARENA.width - c.r, c.x));
  c.y = Math.max(c.r, Math.min(ARENA.height - c.r, c.y));
}

/**
 * Push a circle out of a single rectangle along the minimum translation vector.
 * Returns true if a correction was applied.
 */
export function resolveCircleRect(c: Circle, rect: ObstacleRect): boolean {
  const closestX = Math.max(rect.x, Math.min(c.x, rect.x + rect.w));
  const closestY = Math.max(rect.y, Math.min(c.y, rect.y + rect.h));
  const dx = c.x - closestX;
  const dy = c.y - closestY;
  const distSq = dx * dx + dy * dy;

  if (distSq >= c.r * c.r) return false;

  if (distSq > 1e-6) {
    const dist = Math.sqrt(distSq);
    const push = c.r - dist;
    c.x += (dx / dist) * push;
    c.y += (dy / dist) * push;
    return true;
  }

  // Centre is inside the rectangle: eject along the smallest axis overlap.
  const left = c.x - rect.x;
  const right = rect.x + rect.w - c.x;
  const top = c.y - rect.y;
  const bottom = rect.y + rect.h - c.y;
  const min = Math.min(left, right, top, bottom);
  if (min === left) c.x = rect.x - c.r;
  else if (min === right) c.x = rect.x + rect.w + c.r;
  else if (min === top) c.y = rect.y - c.r;
  else c.y = rect.y + rect.h + c.r;
  return true;
}

/** Resolve a circle against every arena obstacle and the arena bounds. */
export function resolveAgainstArena(c: Circle): boolean {
  let hit = false;
  for (const rect of getActiveObstacles()) {
    if (resolveCircleRect(c, rect)) hit = true;
  }
  clampToBounds(c);
  return hit;
}

/** True if the segment a->b is not blocked by any obstacle (line of sight). */
export function segmentClear(a: Vec2, b: Vec2, pad = 0): boolean {
  for (const o of getActiveObstacles()) {
    if (segmentIntersectsRect(a, b, o, pad)) return false;
  }
  return true;
}

function segmentIntersectsRect(a: Vec2, b: Vec2, rect: ObstacleRect, pad: number): boolean {
  const minX = rect.x - pad;
  const minY = rect.y - pad;
  const maxX = rect.x + rect.w + pad;
  const maxY = rect.y + rect.h + pad;

  const inside = (p: Vec2): boolean => p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
  if (inside(a) || inside(b)) return true;

  const edges: [Vec2, Vec2][] = [
    [
      { x: minX, y: minY },
      { x: maxX, y: minY },
    ],
    [
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
    ],
    [
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
    ],
    [
      { x: minX, y: maxY },
      { x: minX, y: minY },
    ],
  ];
  return edges.some(([p, q]) => segmentsIntersect(a, b, p, q));
}

function segmentsIntersect(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean {
  const d = (a: Vec2, b: Vec2, c: Vec2): number =>
    (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  const d1 = d(p3, p4, p1);
  const d2 = d(p3, p4, p2);
  const d3 = d(p1, p2, p3);
  const d4 = d(p1, p2, p4);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  return false;
}

export interface Body {
  x: number;
  y: number;
  radius: number;
}

/** Separate two bodies ({x,y,radius}) that overlap, moving each half the penetration. */
export function separateBodies(a: Body, b: Body): void {
  separateCircles(
    { x: a.x, y: a.y, r: a.radius },
    { x: b.x, y: b.y, r: b.radius },
    (nx, ny, push) => {
      a.x -= nx * push;
      a.y -= ny * push;
      b.x += nx * push;
      b.y += ny * push;
    },
  );
}

/** Separate two circles that overlap, moving each by half the penetration. */
export function separateCircles(
  a: Circle,
  b: Circle,
  apply?: (nx: number, ny: number, halfPush: number) => void,
): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distSq = dx * dx + dy * dy;
  const min = a.r + b.r;
  if (distSq >= min * min) return;
  const dist = Math.sqrt(distSq) || 0.0001;
  const overlap = (min - dist) / 2;
  const nx = dx / dist;
  const ny = dy / dist;
  if (apply) {
    apply(nx, ny, overlap);
    return;
  }
  a.x -= nx * overlap;
  a.y -= ny * overlap;
  b.x += nx * overlap;
  b.y += ny * overlap;
}
